import { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BookOpen, ClipboardCheck, Library, TrendingUp, Video } from 'lucide-react';
import {
  selectCanShowCoachCalls,
  selectCanShowMyAssessments,
  selectCanShowMyCourses,
  selectCanShowProgress,
  selectFeatureVisibilityReady,
  selectHasVisibleCoachCourse,
} from '../features/appVisibility/store/selectors.js';
import { isB2BUser } from '../features/auth/lib/onboardingStatus.js';
import { cn } from '../lib/cn.js';
import { tapHaptic } from '../services/native/hapticsService.js';

const LABEL_SIDE_GAP = 8;

function NativeTab({ to, label, shortLabel, accessibleLabel, icon, tag, disabled = false }) {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(`${to}/`);
  const tabRef = useRef(null);
  const fullLabelRef = useRef(null);
  const [useShortLabel, setUseShortLabel] = useState(false);

  useLayoutEffect(() => {
    if (!shortLabel) return undefined;
    const tab = tabRef.current;
    const fullLabel = fullLabelRef.current;

    const measure = () => {
      const tabWidth = tab.getBoundingClientRect().width;
      const labelWidth = fullLabel.getBoundingClientRect().width;
      if (tabWidth <= 0 || labelWidth <= 0) return;
      setUseShortLabel(labelWidth + 2 * LABEL_SIDE_GAP > tabWidth);
    };

    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(tab);
    // Always measure the full label, even while showing the short one. This
    // also catches font-size/font-loading changes without toggling in a loop.
    observer?.observe(fullLabel);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [label, shortLabel]);

  return (
    <Link
      ref={tabRef}
      to={disabled ? location.pathname : to}
      aria-label={accessibleLabel}
      aria-current={active ? 'page' : undefined}
      aria-disabled={disabled || undefined}
      onClick={() => {
        if (!disabled) void tapHaptic();
      }}
      className={cn('native-tab', active && 'is-active', disabled && 'is-disabled')}
    >
      {/* The bottom bar has no room for a pill beside the label, so the tag
          rides the icon's top-right corner instead. */}
      <span className="native-tab-icon-wrap">
        {icon}
        {tag && <span className="native-tab-tag">{tag}</span>}
      </span>
      <span className="native-tab-label">{shortLabel && useShortLabel ? shortLabel : label}</span>
      {shortLabel && (
        <span className="native-tab-label-probe" aria-hidden="true">
          <span ref={fullLabelRef}>{label}</span>
        </span>
      )}
    </Link>
  );
}

export function NativeBottomNav({ waitForFeatureVisibility = false }) {
  const canShowCoachCalls = useSelector(selectCanShowCoachCalls);
  const canShowMyCourses = useSelector(selectCanShowMyCourses);
  const canShowMyAssessments = useSelector(selectCanShowMyAssessments);
  const canShowProgress = useSelector(selectCanShowProgress);
  const featureVisibilityReady = useSelector(selectFeatureVisibilityReady);
  const hasCoachingCourse = useSelector(selectHasVisibleCoachCourse);
  const isB2B = useSelector((s) => isB2BUser({ user: s.auth.user, org: s.auth.org }));

  if (waitForFeatureVisibility && !featureVisibilityReady) {
    return <nav className="native-bottom-nav" aria-label="Primary" aria-busy="true" />;
  }

  return (
    <nav className="native-bottom-nav" aria-label="Primary">
      {canShowMyCourses && (
        <NativeTab
          to="/courses"
          label="Courses"
          icon={<BookOpen aria-hidden size={20} strokeWidth={2} />}
        />
      )}
      {canShowMyAssessments && (
        <NativeTab
          to="/assessments"
          label="Assessments"
          icon={<ClipboardCheck aria-hidden size={20} strokeWidth={2} />}
        />
      )}
      {canShowCoachCalls && (
        <NativeTab
          to="/coaching"
          label="Coach calls"
          disabled={!hasCoachingCourse}
          icon={<Video aria-hidden size={20} strokeWidth={2} />}
        />
      )}
      {canShowProgress && (
        <NativeTab to="/progress" label="Progress" icon={<TrendingUp aria-hidden size={20} strokeWidth={2} />} />
      )}
      {!isB2B ? (
        <NativeTab
          to="/library"
          label="More Courses"
          shortLabel="More"
          accessibleLabel="More Courses"
          tag="Buy"
          icon={<Library aria-hidden size={20} strokeWidth={2} />}
        />
      ) : null}
    </nav>
  );
}
